import React from 'react';
import { Search, MessageSquare, CheckCircle, Star, ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

export default function HowItWorks() {
  const customerSteps = [
    { icon: <Search className="w-7 h-7 text-yellow-500" />, step: '01', title: 'Search or Browse', desc: 'Search for what you need or tap a category to instantly see available providers near you.' },
    { icon: <MessageSquare className="w-7 h-7 text-yellow-500" />, step: '02', title: 'Contact a Provider', desc: 'View their profile, check reviews, then reach out via phone, WhatsApp or email.' },
    { icon: <CheckCircle className="w-7 h-7 text-yellow-500" />, step: '03', title: 'Book & Get It Done', desc: 'Agree on time and price, let them do their thing, then confirm it\'s complete.' },
    { icon: <Star className="w-7 h-7 text-yellow-500" />, step: '04', title: 'Leave a Review', desc: 'Help the community by rating your experience and sharing feedback.' },
  ];

  const providerSteps = [
    { icon: '📝', step: '01', title: 'Create a Profile', desc: 'Sign up and list your service — what you do, where you work, your price and availability.' },
    { icon: '🔍', step: '02', title: 'Get Discovered', desc: 'Customers search and find you. Your profile appears in results for your category.' },
    { icon: '💬', step: '03', title: 'Connect with Customers', desc: 'Customers contact you directly. Negotiate, agree on terms, and confirm the job.' },
    { icon: '💰', step: '04', title: 'Get Paid & Grow', desc: 'Do great work, collect payment, earn reviews and build your reputation on HIVE.' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      <Navigation />

      <section className="relative pt-32 md:pt-40 pb-16 overflow-hidden">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-yellow-400/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-black text-gray-900 dark:text-white mb-4">How HIVE Works</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">Simple for customers. Powerful for providers.</p>
        </div>
      </section>

      {/* For Customers */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-yellow-400 rounded-xl flex items-center justify-center text-white font-black text-lg">C</div>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white">For Customers</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {customerSteps.map((s, i) => (
              <div key={i} className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg p-6 relative hover:shadow-2xl hover:scale-[1.03] hover:border-yellow-400/50 transition-all duration-300 group">
                <span className="text-5xl font-black text-gray-100 dark:text-gray-700 absolute top-4 right-4">{s.step}</span>
                <div className="w-12 h-12 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">{s.icon}</div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <button onClick={() => window.location.href = '/'} className="px-8 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-xl font-bold hover:shadow-lg transition-all inline-flex items-center gap-2">
              Find a Service <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* For Providers */}
      <section className="py-16 bg-gray-900/5 dark:bg-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center text-white font-black text-lg">P</div>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white">For Providers</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {providerSteps.map((s, i) => (
              <div key={i} className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg p-6 relative hover:shadow-2xl hover:scale-[1.03] hover:border-green-400/50 transition-all duration-300 group">
                <span className="text-5xl font-black text-gray-100 dark:text-gray-700 absolute top-4 right-4">{s.step}</span>
                <div className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-300">{s.icon}</div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <button onClick={() => window.location.href = '/post-service'} className="px-8 py-3 bg-gradient-to-r from-green-400 to-green-500 text-white rounded-xl font-bold hover:shadow-lg transition-all inline-flex items-center gap-2">
              List Your Service <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}